export const REZRATE_VIDEO_WINDOW_TARGET = 'rezrate-video';
export const REZRATE_VIDEO_POPUP_CONTROL_KEY = 'rezrate-video-popup-control';
export const REZRATE_VIDEO_ACTIVE_SESSION_STORAGE_KEY = 'rezrate-video-active-session';
export const REZRATE_VIDEO_SHELL_PRESENCE_STORAGE_KEY = 'rezrate-video-shell-presence';
export const REZRATE_VIDEO_SHELL_HANDOFF_STORAGE_KEY = 'rezrate-video-shell-handoff';
export const REZRATE_VIDEO_SHELL_HANDOFF_ACK_STORAGE_KEY = 'rezrate-video-shell-handoff-ack';
export const REZRATE_VIDEO_SHELL_PRESENCE_TTL_MS = 15000;
export const REZRATE_VIDEO_IFRAME_ALLOW =
  'camera; microphone; speaker-selection; fullscreen; autoplay; display-capture; screen-wake-lock';

const REZRATE_VIDEO_SESSION_STORAGE_PREFIX = 'rezrate-video-session:';
const REZRATE_VIDEO_SHELL_TAB_ID_SESSION_KEY = 'rezrate-video-shell-tab-id';
const REZRATE_VIDEO_FALLBACK_ALLOWED_ORIGINS = ['https://video.rezrate.com', 'https://devvideo.rezrate.com', 'http://localhost:3001'];

const sanitizeOptionalLaunchUrl = (value = '') => {
  const normalized = `${value || ''}`.trim();
  if (!normalized) {
    return '';
  }

  const lowercase = normalized.toLowerCase();
  if (
    lowercase.includes('undefined') ||
    lowercase.includes('null')
  ) {
    return '';
  }

  return normalized;
};

const getRezrateVideoBuildType = () => `${process.env.REACT_APP_BUILD_TYPE || ''}`.trim();

const getRezrateVideoSessionStorageKey = (sessionId) => {
  return `${REZRATE_VIDEO_SESSION_STORAGE_PREFIX}${sessionId || ''}`;
};

const buildRezrateVideoUrlWithHash = (queryParams, hashParams) => {
  const query = queryParams.toString();
  const hash = hashParams.toString();
  const path = query ? `?${query}` : '';
  return `${constructRezrateVideoUrl(path)}${hash ? `#${hash}` : ''}`;
};

export const constructRezrateVideoBaseUrl = () => {
  const buildType = getRezrateVideoBuildType();

  if (buildType === 'liveproduction') {
    return 'https://video.rezrate.com';
  }

  if (buildType === 'livedevelopment') {
    return 'https://devvideo.rezrate.com';
  }

  return 'http://localhost:3001';
};

export const constructRezrateVideoUrl = (pathOrQuery = '') => {
  const baseUrl = constructRezrateVideoBaseUrl();
  const normalized = `${pathOrQuery || ''}`;

  if (!normalized) {
    return `${baseUrl}/`;
  }

  if (/^https?:\/\//i.test(normalized)) {
    return normalized;
  }

  if (normalized.startsWith('?') || normalized.startsWith('#')) {
    return `${baseUrl}/${normalized}`;
  }

  if (normalized.startsWith('/')) {
    return `${baseUrl}${normalized}`;
  }

  return `${baseUrl}/${normalized}`;
};

export const isAllowedRezrateVideoIframeSrc = (iframeSrc = '') => {
  const normalized = `${iframeSrc || ''}`.trim();
  if (!normalized) {
    return false;
  }

  try {
    const allowedOrigins = new Set(REZRATE_VIDEO_FALLBACK_ALLOWED_ORIGINS);
    allowedOrigins.add(new URL(constructRezrateVideoBaseUrl()).origin);
    const parsed = new URL(normalized, window.location.origin);
    return allowedOrigins.has(parsed.origin);
  } catch (error) {
    return false;
  }
};

export const buildEmbeddedMeetingVideoUrl = (config = {}, options = {}) => {
  const { sessionName, videoSDKJWT, signature, userName, role, topic, Topic } = config || {};
  const token = `${videoSDKJWT || signature || ''}`.trim();
  const normalizedSessionName = `${sessionName || ''}`.trim();
  const topicToUse = `${topic || Topic || ''}`.trim();
  const returnUrl = sanitizeOptionalLaunchUrl(options.returnUrl);
  const refreshUrl = sanitizeOptionalLaunchUrl(options.refreshUrl);

  if (!normalizedSessionName || !token) {
    return '';
  }

  const queryParams = new URLSearchParams();

  const hashParams = new URLSearchParams();
  hashParams.set('sessionName', normalizedSessionName);
  hashParams.set('jwt', token);
  hashParams.set('videoSDKJWT', token);
  hashParams.set('userName', `${userName || ''}`);
  hashParams.set('role', `${role ?? ''}`);
  hashParams.set('topic', topicToUse);
  if (returnUrl) {
    hashParams.set('returnUrl', returnUrl);
  }
  if (refreshUrl) {
    hashParams.set('refreshUrl', refreshUrl);
  }

  return buildRezrateVideoUrlWithHash(queryParams, hashParams);
};

export const buildRezrateEmbeddedMeetingUrl = (config = {}, options = {}) => {
  return buildEmbeddedMeetingVideoUrl(config, options);
};

export const buildGuestEmbeddedMeetingVideoUrl = (guestToken = '', options = {}) => {
  const normalizedGuestToken = `${guestToken || ''}`.trim();
  if (!normalizedGuestToken) {
    return '';
  }

  const joinNonce = `${options.joinNonce || Date.now()}`.trim();
  const queryParams = new URLSearchParams();
  if (joinNonce) {
    queryParams.set('joinNonce', joinNonce);
  }

  const hashParams = new URLSearchParams();
  hashParams.set('guest', normalizedGuestToken);
  hashParams.set('j', normalizedGuestToken);
  hashParams.set('m', normalizedGuestToken);

  return buildRezrateVideoUrlWithHash(queryParams, hashParams);
};

export const createRezrateVideoSessionId = () => {
  const randomSuffix = Math.random().toString(36).slice(2, 10);
  return `rezrate-video-${Date.now()}-${randomSuffix}`;
};

export const createRezrateVideoShellTabId = () => {
  const randomSuffix = Math.random().toString(36).slice(2, 10);
  return `rezrate-video-shell-${Date.now()}-${randomSuffix}`;
};

export const getOrCreateRezrateVideoShellTabId = () => {
  if (typeof window === 'undefined') {
    return createRezrateVideoShellTabId();
  }

  try {
    const existingTabId = `${window.sessionStorage.getItem(REZRATE_VIDEO_SHELL_TAB_ID_SESSION_KEY) || ''}`.trim();
    if (existingTabId) {
      return existingTabId;
    }

    const nextTabId = createRezrateVideoShellTabId();
    window.sessionStorage.setItem(REZRATE_VIDEO_SHELL_TAB_ID_SESSION_KEY, nextTabId);
    return nextTabId;
  } catch (error) {
    return createRezrateVideoShellTabId();
  }
};

export const getRezrateVideoActiveSessionId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return `${window.localStorage.getItem(REZRATE_VIDEO_ACTIVE_SESSION_STORAGE_KEY) || ''}`.trim();
  } catch (error) {
    return '';
  }
};

export const setRezrateVideoActiveSessionId = (sessionId = '') => {
  if (typeof window === 'undefined') {
    return '';
  }

  const normalizedSessionId = `${sessionId || ''}`.trim();

  try {
    if (normalizedSessionId) {
      window.localStorage.setItem(REZRATE_VIDEO_ACTIVE_SESSION_STORAGE_KEY, normalizedSessionId);
    } else {
      window.localStorage.removeItem(REZRATE_VIDEO_ACTIVE_SESSION_STORAGE_KEY);
    }
  } catch (error) {
    // ignore storage errors
  }

  return normalizedSessionId;
};

export const saveRezrateVideoSession = (session) => {
  if (!session || !session.id) {
    return null;
  }

  try {
    localStorage.setItem(getRezrateVideoSessionStorageKey(session.id), JSON.stringify(session));
    return session;
  } catch (error) {
    return null;
  }
};

export const getRezrateVideoSession = (sessionId) => {
  if (!sessionId) {
    return null;
  }

  try {
    const raw = localStorage.getItem(getRezrateVideoSessionStorageKey(sessionId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && parsed.id ? parsed : null;
  } catch (error) {
    return null;
  }
};

export const removeRezrateVideoSession = (sessionId) => {
  if (!sessionId) {
    return;
  }

  try {
    localStorage.removeItem(getRezrateVideoSessionStorageKey(sessionId));
  } catch (error) {
    // ignore storage errors
  }

  if (getRezrateVideoActiveSessionId() === `${sessionId || ''}`.trim()) {
    setRezrateVideoActiveSessionId('');
  }
};

export const createRezrateVideoSession = (payload = {}) => {
  const session = {
    id: createRezrateVideoSessionId(),
    createdAt: Date.now(),
    ...payload,
  };

  return saveRezrateVideoSession(session);
};

export const createOrReuseRezrateVideoSession = (payload = {}, options = {}) => {
  const preferredSessionId = `${options.preferredSessionId || getRezrateVideoActiveSessionId() || ''}`.trim();

  if (preferredSessionId) {
    const existingSession = getRezrateVideoSession(preferredSessionId);
    const nextSession = saveRezrateVideoSession({
      ...(existingSession && existingSession.id ? existingSession : { createdAt: Date.now() }),
      ...payload,
      id: preferredSessionId,
      updatedAt: Date.now(),
    });

    if (nextSession && nextSession.id) {
      setRezrateVideoActiveSessionId(nextSession.id);
    }

    return {
      session: nextSession,
      reusedExisting: !!(existingSession && existingSession.id),
    };
  }

  const nextSession = createRezrateVideoSession(payload);

  if (nextSession && nextSession.id) {
    setRezrateVideoActiveSessionId(nextSession.id);
  }

  return {
    session: nextSession,
    reusedExisting: false,
  };
};

export const buildRezrateVideoShellUrl = (sessionId, options = {}) => {
  void options;
  const normalizedSessionId = `${sessionId || ''}`.trim();
  if (!normalizedSessionId) {
    return '';
  }

  const session = getRezrateVideoSession(normalizedSessionId);
  const iframeSrc = `${session?.iframeSrc || ''}`.trim();

  if (!isAllowedRezrateVideoIframeSrc(iframeSrc)) {
    return '';
  }

  return constructRezrateVideoUrl(iframeSrc);
};

export const buildRezrateVideoShellShareUrl = ({ iframeSrc = '', title = '', absolute = false } = {}) => {
  void title;
  const normalizedIframeSrc = `${iframeSrc || ''}`.trim();
  if (!isAllowedRezrateVideoIframeSrc(normalizedIframeSrc)) {
    return '';
  }

  return absolute
    ? constructRezrateVideoUrl(normalizedIframeSrc)
    : normalizedIframeSrc;
};

export const buildRezrateVideoWindowLaunchUrl = (rawUrl = '', options = {}) => {
  const normalizedUrl = `${rawUrl || ''}`.trim();
  if (!normalizedUrl) {
    return '';
  }

  const shellUrl = buildRezrateVideoShellShareUrl({
    absolute: options.absolute,
    iframeSrc: normalizedUrl,
    title: options.title,
  });

  return shellUrl || normalizedUrl;
};

export const parseRezrateVideoShellLocation = (search = '') => {
  const query = new URLSearchParams(search || '');
  const sessionId = `${query.get('session') || ''}`.trim();
  const title = `${query.get('title') || ''}`.trim();
  const rawIframeSrc = `${query.get('src') || ''}`.trim();

  return {
    sessionId,
    title,
    iframeSrc: isAllowedRezrateVideoIframeSrc(rawIframeSrc) ? rawIframeSrc : '',
  };
};

export const requestRezrateVideoPopupClose = (sessionId = '') => {
  const payload = {
    type: 'REQUEST_POPUP_CLOSE',
    sessionId: `${sessionId || ''}`.trim(),
    target: REZRATE_VIDEO_WINDOW_TARGET,
    timestamp: Date.now(),
  };

  try {
    localStorage.setItem(REZRATE_VIDEO_POPUP_CONTROL_KEY, JSON.stringify(payload));
  } catch (error) {
    // ignore storage errors
  }

  return payload;
};

export const parseRezrateVideoPopupControl = (rawValue) => {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};
